import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import type {
	ChatMessageEventHandlers,
	ErrorPayload,
	WidgetConfig,
} from '@atlassian/customer-service-management-widget-sdk-types';

import type { ChatInput, CSMWidgetEmbedSDK } from '../../common/types/sdk';
import { loadCSMSDK } from '../../common/utils/load-js-sdk';
import { useEvent } from '../../controllers/use-event';
import { useLatestHandlers } from '../../controllers/use-latest';

type SDKRef = React.RefObject<CSMWidgetEmbedSDK | null>;

type DebugPayload = {
	message: string;
};

type CSMWidgetEmbedProps = {
	/**
	 * The settings for the widget to be embedded
	 */
	settings: WidgetConfig & {
		/**
		 * Custom uiConfig for the widget.
		 */
		uiConfig?: {
			/**
			 * Error boundary message.
			 */
			errors?: {
				message?: string;
			};
			/**
			 * Chat input field placeholder message.
			 */
			chatInput?: ChatInput;
		};
		/**
		 * Analytics context to pass to the widget
		 */
		analyticsContext?: Record<string, unknown>;
	};
	/**
	 * Provides a ref to access imperative actions of the widget.
	 * The SDK is only available after it has successfully been loaded.
	 */
	sdk?: SDKRef;
	/**
	 * Callback to run when the widget status changes.
	 */
	onChatStatus?: (status: 'online' | 'offline' | 'launching') => void;
	/**
	 * Callbacks run after user attempts to send a message.
	 */
	onChatMessage?: ChatMessageEventHandlers;
	/**
	 * Callback to run when an error occurs.
	 */
	onError?: (error: ErrorPayload) => void;
	/**
	 * Callback to run when a debug message is received.
	 */
	onDebug?: (payload: DebugPayload) => void;
};

const NOOP = () => {};

const stringifyError = (error: unknown) => {
	if (typeof error === 'string') {
		return error;
	}

	if (error instanceof Error) {
		return error.message;
	}

	return 'Unknown error';
};

export const CSMWidgetEmbed = ({
	settings: settingsProp,
	sdk,
	onChatStatus: onChatStatusProp,
	onChatMessage,
	onError: onErrorProp,
	onDebug: onDebugProp,
}: CSMWidgetEmbedProps): React.JSX.Element => {
	const [settings] = useState(settingsProp);
	const containerRef = useRef<HTMLDivElement>(null);
	const [embed, setEmbed] = useState<CSMWidgetEmbedSDK | null>(null);
	const chatMessageEventHandlers = useLatestHandlers(onChatMessage);
	const onChatStatus = useEvent(onChatStatusProp ?? NOOP);
	const onError = useEvent(onErrorProp ?? NOOP);
	const debug = useEvent(onDebugProp ?? NOOP);

	useEffect(() => {
		debug({
			message: '[ReactSDK]: mounted',
		});

		return () => {
			debug({
				message: '[ReactSDK]: unmounted',
			});
		};
	}, [debug]);

	useEffect(() => {
		const bindEmbedInstance = async () => {
			try {
				debug({
					message: '[ReactSDK]: loading CSM SDK',
				});

				const CSM = await loadCSMSDK({ site: settings.site, debug });

				if (!containerRef.current) {
					debug({
						message: '[ReactSDK]: embed target container ref not found',
					});
					throw new Error('Container element not found');
				}

				debug({
					message: '[ReactSDK]: creating CSM.embeddableWidget instance',
				});

				const instance = await CSM.embeddableWidget(containerRef.current, settings, {
					debug: (message) => {
						debug({
							message,
						});
					},
				});

				if (instance instanceof Error) {
					debug({
						message: `[ReactSDK]: embeddableWidget returned error - ${instance.message}`,
					});
					onError({
						message: instance.message,
					});
					return;
				}

				const events = ['before', 'started', 'aborted', 'completed', 'failed'] satisfies Array<
					keyof ChatMessageEventHandlers
				>;

				const bindEvent = <Event extends keyof ChatMessageEventHandlers>(event: Event) => {
					instance.on(`message:${event}`, (...args: any[]) => {
						// @ts-ignore
						chatMessageEventHandlers.current?.[event]?.(...args);
					});
				};

				events.forEach((event) => {
					bindEvent(event);
				});

				instance.on('status', (payload: { status: string }) => {
					debug({
						message: `[ReactSDK]: received status message ${payload.status}`,
					});
					onChatStatus(payload.status as 'online' | 'offline' | 'launching');
				});

				instance.on('error', (error: ErrorPayload) => {
					debug({
						message: '[ReactSDK]: received error message',
					});
					onError(error);
				});

				setEmbed(instance);
				debug({
					message: '[ReactSDK]: embed setup complete',
				});
			} catch (error: unknown) {
				debug({
					message: '[ReactSDK]: error caught',
				});
				onError({
					message: stringifyError(error),
				});
			}
		};

		bindEmbedInstance();
	}, [settings, chatMessageEventHandlers, onChatStatus, onError, debug]);

	useImperativeHandle(sdk, () => embed, [embed]);

	// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
	return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};
